// 3rd party
import React, { useState } from "react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { Grid, CircularProgress } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { Card } from "../common/card/card";
import { Header } from "../common/title/header";
import { useWorkOrderStyles, Caption, Value, CardTitle } from "./workOrderDetails";
import AttachmentsInfo from "./attachments/attachmentsInfo";
import WorkOrderCustomerInfo from "./workOrderCustomerInfo";
import WorkOrderJobOverviewInfo from "./workOrderJobOverviewInfo";
import WorkOrderJobDescriptionInfo from "./workOrderJobDescriptionInfo";
import WorkOrderActivityInfo from "./workOrderActivityInfo";
import WorkOrderCreationInformation from "./workOrderCreationInformation";
import WorkOrderAssignementInfo from "./workOrderAssignementInfo.jsx";
import { isMarkedAsIncomplete } from "../../globals/workOrderStatus";
import { useLocalizedName } from "../../globals/langHelpers";
import WorkPermitInfo from "./workPermit/workPermitInfo";

const useStyles = makeStyles((theme) => ({
    alert: {
        width: "100%",
        backgroundColor: "white",
        color: "#111111",
        padding: 0,
        margin: "1rem 0",
        "& .MuiAlert-icon": {
            backgroundColor: "#ff9800",
            color: "#ffffff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 8,
        },
    },
    root: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        height: "50vh",
    },
}));

const WorkOrderInformation = (props) => {
    const { workOrderCancelledReasons } = props;
    const { workorderData, multipleWorkActivityInformation, relatedIdInformation } = props?.workorderInfo;
    const classes = useWorkOrderStyles();
    const alertClass = useStyles();
    const { getLocalizedName } = useLocalizedName();
    const [editPermit, setEditPermit] = useState(false);
    const onCancel = () => {
        setEditPermit(false);
    };
    const onEdit = () => {
        setEditPermit(true);
    };
    let multipleWorkActivity = multipleWorkActivityInformation
        ? multipleWorkActivityInformation?.body
            ? multipleWorkActivityInformation?.body
            : null
        : null;
    const comment = props?.comments?.filter((comment) => comment?.incompletionComment).map((comment) => comment)[0] ?? "";
    const intl = useIntl();

    return (
        <>
            {workorderData?.resourceAssigned && isMarkedAsIncomplete(workorderData?.status) && (
                <Alert className={alertClass.alert} variant="outlined" severity="warning">
                    <div>
                        <em>
                            <FormattedMessage
                                id="admin.work-order-details.incomplete.details"
                                defaultMessage="This job was incompleted on {incompleteDate}, by {firstName} {lastName} due to {reasonCode}{subReasonCode}."
                                description="WO Incomplete details"
                                values={{
                                    incompleteDate: moment(workorderData?.incompletionDate).format("LL"),
                                    firstName: comment?.firstName,
                                    lastName: comment?.lastName,
                                    reasonCode: getLocalizedName({ object: comment?.reasonCode }),
                                    subReasonCode:
                                        comment?.subReasonCode && getLocalizedName({ object: comment?.subReasonCode })
                                            ? " - " + getLocalizedName({ object: comment?.subReasonCode })
                                            : "",
                                }}
                            />
                        </em>
                    </div>
                    <div>
                        <FormattedMessage
                            id="admin.work-order-details.incomplete.labourer-comments"
                            defaultMessage="Additional details: {labourerComments}"
                            description="WO Incomplete labourer comments"
                            values={{
                                labourerComments: comment?.comment || "",
                            }}
                        />
                    </div>
                </Alert>
            )}
            {workOrderCancelledReasons && Object?.keys(workOrderCancelledReasons)?.length > 0 && (
                <Alert className={alertClass.alert} variant="outlined" severity="warning">
                    <div>
                        <em>
                            <FormattedMessage
                                id="common.cancelled.details"
                                defaultMessage="The job was cancelled on {date} by {user} due to {reason}."
                                description="WO cancelled details"
                                values={{
                                    date: workOrderCancelledReasons?.createdDate && moment(workOrderCancelledReasons?.createdDate).format("LL"),
                                    user: workOrderCancelledReasons?.createdBy,
                                    reason:
                                        intl.locale === "en"
                                            ? workOrderCancelledReasons?.reasonCode?.name_en
                                            : workOrderCancelledReasons?.reasonCode?.name_fr,
                                }}
                            />
                        </em>
                    </div>
                    <div>
                        <FormattedMessage
                            id="common.cancelled.comments"
                            defaultMessage="Additional details: {comments}"
                            description="WO cancelled comments"
                            values={{
                                comments: workOrderCancelledReasons?.comment,
                            }}
                        />
                    </div>
                </Alert>
            )}

            {Object.keys(workorderData).length <= 0 ? (
                <div className={classes.spinner}>
                    <CircularProgress />
                </div>
            ) : (
                relatedIdInformation?.tasks?.workType && (
                    <>
                        <WorkOrderJobOverviewInfo
                            workOrderData={workorderData}
                            vendorManager={props?.vendorManager}
                            relatedIdInformation={relatedIdInformation}
                            cancelledReason={props?.workOrderCancelledReasons}
                        />
                        <WorkOrderCustomerInfo workOrderData={workorderData} relatedIdInformation={relatedIdInformation} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Card>
                                    <CardTitle>
                                        <FormattedMessage
                                            id="admin.work-order-details.wo-info.comments-title"
                                            defaultMessage="Comments"
                                            description="Admin work order comments title"
                                        />
                                    </CardTitle>
                                    <Grid container className={classes.row}>
                                        <Grid item>
                                            <Caption>{relatedIdInformation ? relatedIdInformation?.additionalComments : ""}</Caption>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        </Grid>
                        <WorkOrderCreationInformation relatedIdInformation={relatedIdInformation} />
                    </>
                )
            )}
            <WorkOrderAssignementInfo
                customerPhoneNumber={relatedIdInformation?.accessId ? relatedIdInformation?.accessId : relatedIdInformation?.customerPhoneNumber}
                isAdmin={true}
                assignmentInformation={props?.assignmentInfo}
            />
            {relatedIdInformation?.tasks?.workType && (
                <>
                    <WorkOrderJobDescriptionInfo relatedIdInformation={relatedIdInformation} />
                    {multipleWorkActivity !== null &&
                        typeof multipleWorkActivity !== "string" &&
                        multipleWorkActivity.map((WorkActivitydetails, i) => {
                            return (
                                <WorkOrderActivityInfo
                                    key={WorkActivitydetails?.id || i}
                                    count={i + 1}
                                    index={i}
                                    WorkActivitydetails={WorkActivitydetails}
                                />
                            );
                        })}
                </>
            )}
            {relatedIdInformation?.additionalComments ? (
                relatedIdInformation.additionalComments !== "" ? (
                    <div className="no-print">
                        <Grid item xs={12}>
                            <Card>
                                <CardTitle>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.follow-up-comments"
                                        defaultMessage="Follow-up Comments"
                                        description="Follow-up Comments"
                                    />
                                </CardTitle>
                                <Grid container>
                                    <Grid item>
                                        <Caption>{relatedIdInformation?.additionalComments}</Caption>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </div>
                ) : null
            ) : null}

            {process.env.REACT_APP_FEATURE_CMISR3 === "true" && relatedIdInformation?.tasks?.workType && (
                <WorkPermitInfo relatedIdInformation={relatedIdInformation} edit={editPermit} onCancel={onCancel} onEdit={onEdit} />
            )}
            {process.env.REACT_APP_FEATURE_FILEUPLOAD === "true" && (
                <>
                    <div className="no-print">
                        <Header>
                            <FormattedMessage
                                id="admin.work-order-details.wo-info.attachments-title"
                                defaultMessage="Attachments"
                                description="Admin work order attachments title"
                            />
                        </Header>
                        <Card style={{ overflowX: "auto" }}>
                            <Grid container>
                                <AttachmentsInfo
                                    workOrderNumber={relatedIdInformation?.workOrderId}
                                    clickDispatchId={relatedIdInformation?.tasks?.clickDispatchId}
                                />
                            </Grid>
                        </Card>
                    </div>
                </>
            )}
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        vendorManager: state.tasks.vendorManagerByOrder,
        workorderInfo: state.tasks.workorderData,
        assignmentInfo: state.tasks.assignmentInformation,
        comments: state.billing.comments,
        workOrderCancelledReasons: state.tasks?.workOrderCancelledReasons,
    };
};
export default connect(mapStateToProps, null)(WorkOrderInformation);
